/**
 * Hook for managing conversation sidebar state and operations
 * Can be used in any agent chat component
 */

import { useState, useCallback } from 'react';
import { ConversationMessage } from '../../services/conversationService';

export interface ConversationLoadMeta {
  dataSourceName?: string;
}

interface UseConversationSidebarReturn {
  isSidebarOpen: boolean;
  openSidebar: () => void;
  closeSidebar: () => void;
  toggleSidebar: () => void;
  currentConversationId: string | undefined;
  handleSelectConversation: (conversationId: string, messages: ConversationMessage[], meta?: ConversationLoadMeta) => void;
  handleStartNewConversation: () => void;  // Start a new conversation (clears current conversation)
}

export function useConversationSidebar(
  onMessagesLoaded?: (messages: ConversationMessage[], conversationId?: string, meta?: ConversationLoadMeta) => void
): UseConversationSidebarReturn {
  // Conversation sidebar is hidden by default. Users open it explicitly via
  // the "Conversation history" toggle in the bot chat header. We deliberately
  // do NOT auto-open on first mount or on a page refresh — landing in the
  // bot should always show the chat itself, not a sidebar that hides it.
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [currentConversationId, setCurrentConversationId] = useState<string | undefined>();

  const openSidebar = useCallback(() => {
    setIsSidebarOpen(true);
  }, []);

  const closeSidebar = useCallback(() => {
    setIsSidebarOpen(false);
  }, []);

  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen(prev => !prev);
  }, []);

  const handleSelectConversation = useCallback((conversationId: string, messages: ConversationMessage[], meta?: ConversationLoadMeta) => {
    setCurrentConversationId(conversationId);
    if (onMessagesLoaded) {
      onMessagesLoaded(messages, conversationId, meta);
    }
  }, [onMessagesLoaded]);

  // Start a new conversation - clears current conversation ID and messages
  const handleStartNewConversation = useCallback(() => {
    console.log('[useConversationSidebar] Starting new conversation, clearing currentConversationId');
    setCurrentConversationId(undefined);
    if (onMessagesLoaded) {
      // Pass empty messages and no conversation ID to signal new conversation
      onMessagesLoaded([], undefined);
    }
    // Close sidebar after starting new conversation
    setIsSidebarOpen(false);
  }, [onMessagesLoaded]);

  return {
    isSidebarOpen,
    openSidebar,
    closeSidebar,
    toggleSidebar,
    currentConversationId,
    handleSelectConversation,
    handleStartNewConversation,
  };
}






